import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '@/lib/api';
import type { TrackerSheetSortOutcome } from '@/components/tracker-sheet/TrackerSheetPanel';
import type { ClientDeliverableSheetSort } from '@/lib/types';

/**
 * HOW THE CLIENT DELIVERABLES MIRROR IS TOLD TO ORDER ITS TAB.
 *
 * ── 🔴 A SEPARATE MODULE FROM `use-tracker-sheet.ts`, AND NOT FOR TIDINESS ──
 *
 * That one reads `GET /client-deliverables/sheet` through a MODULE-LEVEL CACHE
 * held for the life of the tab, and its header states the reason in as many
 * words: *"The value is derived from two environment variables on the server. It
 * cannot change under a session; it changes when somebody redeploys."*
 *
 * ⚠️ **THAT SENTENCE IS TRUE OF THE ADDRESS AND FALSE OF THIS SETTING.** An
 * administrator edits this one, through this app, and expects the control to
 * show what they just chose. Folding it into that read would not break the cache
 * — it would FALSIFY THE SENTENCE THAT JUSTIFIES THE CACHE, and the symptom
 * would be a dropdown that reverts to a stale value the next time somebody
 * navigates back to the tracker.
 *
 * So: no module-level cache, one request per mount of the page that renders the
 * control, and the sidebar — which mounts on every signed-in route — pays
 * nothing for it.
 *
 * ── EVERY FAILURE DEGRADES TO "NO CONTROL", NEVER TO AN ERROR STATE ────────
 *
 * ⚠️ **A 404 IS THE ORDINARY ANSWER FROM A SERVER PREDATING THIS ROUTE**, not a
 * fault. This app deploys separately from the API and can be newer than it, so a
 * frontend shipped ahead of the backend must simply not offer the control. A
 * failed read is treated the same way, for the reason `use-tracker-sheet`
 * records about its own: this is an optional way to arrange an optional
 * document, and a red banner about it would be louder than the thing it reports.
 *
 * ⚠️ **THE SAVE IS THE OPPOSITE AND MUST NEVER BE SILENT.** A read nobody asked
 * for may fail quietly; a write somebody just made may not. Every branch of
 * `save` returns a sentence.
 */

/** What a caller sees. `stored` is `null` until a read succeeds, and after any failure. */
export interface TrackerSheetSortState {
  loading: boolean;
  /**
   * ⚠️ **THREE STATES, NOT TWO, AND `available` IS WHAT SEPARATES THEM.**
   * `stored: null` means either "still asking" or "this API has no such
   * setting"; only `available` tells them apart, and only `available` may gate
   * whether the control renders. Rendering on `stored !== null` alone would flash
   * the control in and out on every mount.
   */
  stored: ClientDeliverableSheetSort | null;
  /** Whether the endpoint answered at all. False while loading, and after a 404. */
  available: boolean;
  save: (choice: ClientDeliverableSheetSort) => Promise<TrackerSheetSortOutcome>;
}

/**
 * ⚠️ **THE SENTENCES LIVE HERE, IN ONE PLACE, AND THEY ARE NOT INTERCHANGEABLE.**
 * Each names a different thing for the reader to do, and the difference between
 * the first two is the one that costs somebody a second attempt at a save that
 * already worked.
 */
const SAVED_AND_SYNCED = 'Saved, and the sheet has been rewritten.';
const SAVED_NOT_SYNCED =
  'Saved. The sheet has not been rewritten yet — it will re-sort on the next sync.';
const SAVED_SYNC_FAILED =
  'Saved, but rewriting the sheet just now did not work. It will re-sort on the next sync.';
const NOT_SAVED = 'That could not be saved, so the sheet is unchanged.';

export function useTrackerSheetSort(enabled: boolean): TrackerSheetSortState {
  const [state, setState] = useState<{
    loading: boolean;
    stored: ClientDeliverableSheetSort | null;
    available: boolean;
  }>({ loading: enabled, stored: null, available: false });

  useEffect(() => {
    if (!enabled) {
      // Derived rather than written from the effect for the reason
      // `use-tracker-sheet` records: setting state here starts a second render
      // synchronously. `loading: false` because "nobody asked" is a settled
      // answer, and a caller waiting on it would wait for ever.
      return;
    }

    const controller = new AbortController();

    api
      .clientDeliverableSheetSort(controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        /*
         * ⚠️ `?? null` RATHER THAN `response.data` STRAIGHT THROUGH. The
         * contract says `data` is always present; what that does not cover is a
         * 200 carrying a body we did not expect — an older server, a proxy
         * answering for it, a shape change. A resolved `undefined` reaching the
         * panel would be read for `.sort` inside a render, which React
         * escalates to unmounting the tree: the whole tracker page goes blank
         * because a dropdown could not be filled in.
         */
        const data = response.data ?? null;
        setState({ loading: false, stored: data, available: data !== null });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        // Every failure is "no control", including the 404 from a server that
        // predates the route. See the header.
        setState({ loading: false, stored: null, available: false });
      });

    return () => {
      controller.abort();
    };
  }, [enabled]);

  /**
   * Store the choice, then offer to apply it immediately.
   *
   * 🔴 **THE TWO CALLS ARE REPORTED SEPARATELY AND THE ORDER IS LOAD-BEARING.**
   * The setting is stored first and the rewrite is attempted second, so a
   * Google failure can never cost somebody the choice they just made. **A sync
   * that fails after a store that succeeded is NOT a failed save**, and saying
   * so would be the lie a person acts on — by choosing again, which stores the
   * same value and tries the same rewrite.
   *
   * ⚠️ **`status: 'skipped'` IS A 200 AND IS NOT A SYNC.** An unconfigured
   * mirror and a day between two cycles both land there. Reading the status
   * code rather than the body is this codebase's most repeated mistake — three
   * times on Xero alone — so the `'synced'` sentence is emitted for that literal
   * and for nothing else.
   *
   * ⚠️ **NO `AbortSignal` ON EITHER CALL, DELIBERATELY.** Both are writes the
   * person explicitly asked for: a `PUT` aborted on unmount would leave the
   * store in an unknown state with nobody to tell, and an aborted sync would
   * leave a tab half-rewritten. The house rule exists so a component's fetch
   * does not outlive it and set state on a dead component — which is handled
   * instead by the panel owning the outcome, and by this promise resolving to a
   * value rather than writing anything itself.
   */
  const save = useCallback(
    async (choice: ClientDeliverableSheetSort): Promise<TrackerSheetSortOutcome> => {
      try {
        const stored = await api.setClientDeliverableSheetSort(choice);
        // The SERVER's answer, not the choice that was sent — the two are the
        // same today and stop being the same the moment anything normalises on
        // the way down.
        setState({ loading: false, stored: stored.data ?? choice, available: true });
      } catch (error) {
        return {
          tone: 'error',
          /*
           * The server's own sentence where there is one, ours only where there
           * is not. A 403 carries no `details`, so it renders as this sentence
           * plus whatever the API said — never as a field error.
           */
          message:
            error instanceof ApiError && error.message
              ? `${NOT_SAVED} ${error.message}`
              : NOT_SAVED,
        };
      }

      try {
        const run = await api.syncClientDeliverableSheet();
        return run.data?.status === 'synced'
          ? { tone: 'ok', message: SAVED_AND_SYNCED }
          : { tone: 'warning', message: SAVED_NOT_SYNCED };
      } catch {
        /*
         * ⚠️ `warning`, NEVER `error`. The choice IS stored — the line above
         * proved it — so this reports on the rewrite alone. An `error` here
         * would say the save failed, which is false, and the reader would act on
         * it.
         */
        return { tone: 'warning', message: SAVED_SYNC_FAILED };
      }
    },
    [],
  );

  return enabled
    ? { ...state, save }
    : { loading: false, stored: null, available: false, save };
}
